import type { ReactNode } from 'react';
import { Icon, type IconName } from '@garnet/ui';

/**
 * StateBlock — empty / abstain / no-coverage placeholder. The Core stylesheet ships no
 * `.statebox`, so this is a faithful local port of the back-office StateBlock: a dashed
 * panel with a mono headline. The `abstain` kind carries the amber treatment because
 * abstention is a designed state, not an error.
 */
export type StateBlockKind = 'empty' | 'abstain' | 'nocover';

const MAP: Record<StateBlockKind, { color: string; icon: IconName; border: string; bg: string }> = {
  empty: { color: 'var(--fg-3)', icon: 'grid', border: 'var(--border)', bg: 'transparent' },
  abstain: {
    color: 'var(--abstain-text)',
    icon: 'shield',
    border: 'var(--abstain-border)',
    bg: 'var(--abstain-bg)',
  },
  nocover: { color: 'var(--fg-3)', icon: 'x', border: 'var(--border)', bg: 'transparent' },
};

export function StateBlock({
  kind = 'empty',
  title,
  body,
  action,
}: {
  kind?: StateBlockKind;
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
}) {
  const m = MAP[kind];
  return (
    <div
      style={{
        border: `1px dashed ${m.border}`,
        borderRadius: 6,
        padding: '22px 18px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        textAlign: 'center',
        background: m.bg,
      }}
    >
      <Icon name={m.icon} size={20} color={m.color} stroke={1.4} />
      <div className="mono" style={{ fontSize: 13, color: m.color, fontWeight: 500 }}>
        {title}
      </div>
      {body && (
        <div className="metatext" style={{ maxWidth: 420 }}>
          {body}
        </div>
      )}
      {action}
    </div>
  );
}
